import React, { useState, useEffect } from "react";
import { Grid, CircularProgress, Backdrop, Snackbar, Button, Paper, Typography, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Add as AddIcon } from "@material-ui/icons";
import MapView from "./components/MapView";
import CropHealthPanel from "./components/CropHealthPanel";
import RiskAssessmentPanel from "./components/RiskAssessmentPanel";
import MetricsPanel from "./components/MetricsPanel";
import ApplicantUpload from "./components/ApplicantUpload";
import ApplicantSelector from "./components/ApplicantSelector";
import ApplicantDetails from "./components/ApplicantDetails";
import CreateFarmModal from "./components/CreateFarmModal";
import PageTitle from "../../components/PageTitle";
import apiService from "../../services/api";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  topRow: {
    marginBottom: theme.spacing(3)
  },
  mapContainer: {
    height: "400px",
    width: "100%",
    marginBottom: theme.spacing(3)
  },
  metricsContainer: {
    marginBottom: theme.spacing(3)
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  loadingPanel: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: theme.shape.borderRadius
  },
  createButton: {
    marginLeft: theme.spacing(2),
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    }
  },
  selectorContainer: {
    display: 'flex',
    alignItems: 'center'
  }
}));

// Custom notification component using existing Material-UI components
function Notification({ severity, children, action }) {
  const getColor = (type) => {
    switch(type) {
      case "error": return "#f44336";
      case "warning": return "#ff9800";
      case "info": return "#2196f3";
      case "success": return "#4caf50";
      default: return "#2196f3";
    }
  };
  
  return (
    <Paper 
      style={{ 
        padding: '16px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        backgroundColor: getColor(severity),
        color: 'white'
      }}
    >
      <Typography variant="body1">{children}</Typography>
      {action && <div>{action}</div>}
    </Paper>
  );
}

export default function EOAnalysisDashboardFarmer() {
  const classes = useStyles();
  const [applicants, setApplicants] = useState([]);
  const [currentApplicant, setCurrentApplicant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [createFarmModalOpen, setCreateFarmModalOpen] = useState(false);

  // Load farm data when component mounts
  useEffect(() => {
    const fetchFarms = async () => {
      try {
        setIsLoading(true);
        setError(null);
                
        // Check API health first
        const isHealthy = await apiService.healthCheck();
        if (!isHealthy) {
          throw new Error('API health check failed');
        }
        
        // Get farm list from API
        const farmsData = await apiService.getFarms();
        
        if (!farmsData || farmsData.length === 0) {
          setError('No farms found. Please create a farm or contact support.');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }
        
        setApplicants(farmsData);
        
        // Set the first farm as current if we have data
        if (farmsData && farmsData.length > 0) {
          // Get full analysis for the first farm
          const farmAnalysis = await apiService.getFarmFullAnalysis(farmsData[0].id);
          setCurrentApplicant(farmAnalysis);
        }
      } catch (err) {
        console.error('Failed to fetch farms:', err);
        setError('Failed to load farm data from API. Please check your connection and try again.');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFarms();
  }, []);


  const handleApplicantsUploaded = (newApplicants) => {
    setIsLoading(true);
    // In a real implementation, we would send this data to the API
    // and then retrieve the updated data
    setTimeout(() => {
      setApplicants([...applicants, ...newApplicants]);
      if (newApplicants.length > 0) {
        setCurrentApplicant(newApplicants[0]);
      }
      setIsLoading(false);
    }, 1000);
  };

  const handleApplicantSelect = async (applicant) => {
    if (!applicant || !applicant.id) {
      console.error('Invalid applicant selected:', applicant);
      setError('Unable to select farm: Invalid farm data');
      setSnackbarOpen(true);
      return;
    }
    
    console.log(`Farm selection changed to: ${applicant.id} (${applicant.name || 'Unnamed Farm'})`);
    
    try {
      setIsLoading(true);
      
      // Get full analysis for the selected farm
      console.log(`Fetching full analysis for farm ${applicant.id}`);
      const farmAnalysis = await apiService.getFarmFullAnalysis(applicant.id);
      
      // Validate that the returned data matches the requested farm
      if (farmAnalysis.id !== applicant.id) {
        console.error(`Farm ID mismatch! Requested ${applicant.id} but API returned ${farmAnalysis.id}`);
        // Force the correct ID
        farmAnalysis.id = applicant.id;
      }
      
      console.log(`Setting current applicant to farm ${farmAnalysis.id}`);
      setCurrentApplicant(farmAnalysis);
    } catch (err) {
      console.error(`Failed to fetch details for farm ${applicant.id}:`, err);
      setError(`Failed to load data for farm ${applicant.name || applicant.id}. Please try again later.`);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  const handleRetryConnection = async () => {
    setError(null);
    setIsLoading(true);
    
    try {
      // Check API health
      const isHealthy = await apiService.healthCheck();
      if (!isHealthy) {
        throw new Error('API health check failed');
      }
      
      // Get farm list from API
      const farmsData = await apiService.getFarms();
      
      if (!farmsData || farmsData.length === 0) {
        setError('No farms found. Please create a farm or contact support.');
        setSnackbarOpen(true);
        return;
      }
      
      setApplicants(farmsData);
      
      // Set the first farm as current if we have data
      if (farmsData && farmsData.length > 0) {
        // Get full analysis for the first farm
        const farmAnalysis = await apiService.getFarmFullAnalysis(farmsData[0].id);
        setCurrentApplicant(farmAnalysis);
      }
    } catch (err) {
      console.error('Failed to fetch farms during retry:', err);
      setError('Failed to load farm data from API. Please check your connection and try again.');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateFarmModal = () => {
    setCreateFarmModalOpen(true);
  };

  const handleCloseCreateFarmModal = () => {
    setCreateFarmModalOpen(false);
  };

  const handleFarmCreated = (newFarm) => {
    console.log('New farm created:', newFarm);
    
    // Add the new farm to the applicants list
    setApplicants(prevApplicants => [
      ...prevApplicants,
      newFarm
    ]);
    
    // Set it as the current applicant
    setCurrentApplicant(newFarm);
    
    // Show a success message
    setError(`Farm "${newFarm.name}" created successfully`);
    setSnackbarOpen(true);
  };

  const LoadingOverlay = () => (
    <div className={classes.loadingPanel}>
      <CircularProgress />
    </div>
  );

  return (
    <>
      <PageTitle title="EO Farmer Analysis Dashboard" />
      <div className={classes.root}>
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Notification
              severity="warning"
              action={
                <Button variant="contained" size="small" onClick={handleRetryConnection}>
                  Retry
                </Button>
              }
            >
              {error}
            </Notification>
          </div>
        )}
        <Grid container spacing={4}>
          <Grid item xs={12} className={classes.topRow}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <div className={classes.selectorContainer}>
                  <ApplicantSelector
                    applicants={applicants}
                    currentApplicantId={currentApplicant ? currentApplicant.id : ''}
                    onSelectApplicant={handleApplicantSelect}
                    isLoading={isLoading}
                  />
                  <Button
                    variant="contained"
                    className={classes.createButton}
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateFarmModal}
                    disabled={isLoading}
                  >
                    Create Farm
                  </Button>
                </div>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <ApplicantUpload 
                  onApplicantsUploaded={handleApplicantsUploaded}
                  isLoading={isLoading}
                />
              </Grid> */}
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            {isLoading ? (
              <LoadingOverlay />
            ) : (
              <ApplicantDetails data={currentApplicant || {}} />
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            <div className={classes.mapContainer}>
              {isLoading ? <LoadingOverlay /> : (
                <MapView 
                  farmId={currentApplicant?.id} 
                  farmLocation={currentApplicant?.location} 
                />
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            {isLoading ? (
              <LoadingOverlay />
            ) : (
              <CropHealthPanel data={currentApplicant || {}} />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isLoading ? (
              <LoadingOverlay />
            ) : (
              <RiskAssessmentPanel data={currentApplicant || {}} />
            )}
          </Grid>
          <Grid item xs={12}>
            <div className={classes.metricsContainer}>
              {isLoading ? (
                <LoadingOverlay />
              ) : (
                <MetricsPanel data={currentApplicant || {}} />
              )}
            </div>
          </Grid>
        </Grid>
      </div>
      <Backdrop className={classes.backdrop} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Paper style={{ padding: '8px 16px', backgroundColor: '#ff9800', color: 'white' }}>
          <Typography>{error}</Typography>
        </Paper>
      </Snackbar>

      {/* Create Farm Modal */}
      <CreateFarmModal
        open={createFarmModalOpen}
        onClose={handleCloseCreateFarmModal}
        onFarmCreated={handleFarmCreated}
      />
    </>
  );
}